import React, { useState, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Select,
  Button,
  Spin,
  Alert,
  Input,
  Tag,
  message,
  Tooltip,
  Badge,
} from 'antd';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  RiseOutlined,
  FallOutlined,
  BarChartOutlined,
  SearchOutlined,
  ShopOutlined,
  ApartmentOutlined,
  AppstoreOutlined,
  RadarChartOutlined,
  DashboardOutlined,
  SplitCellsOutlined,
  LineChartOutlined,
  DotChartOutlined,
  NodeIndexOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetAdminBrandsForAnalyticsQuery,
  useGetBrandBenchmarksQuery,
  useCalculateBenchmarksMutation,
  useGetBenchmarkingPreferencesQuery,
  useUpdateBenchmarkingPreferencesMutation,
  useGetAdminBenchmarkBusinessTypesQuery,
} from '../../services/nestjs-base-api';
import { PerformanceChart, PolarChart, MetricsCards, BulletChart, DivergingBarChart, SlopeChart, ScatterPlot, StripChart, BenchmarkMetric } from '../../components/charts';
import { getPerformanceLevel, calculateAbsoluteDifference } from '../../components/charts/benchmarkThresholds';
import { getDisplayName } from '../../utils/elementDisplayNames';
import { useAuth } from '../../hooks/useAuth';

// Types

export interface BenchmarkResponse {
  brandPerformance: {
    overallScore: number;
    overallPercentile: number;
    metrics: BenchmarkMetric[];
    strengths: BenchmarkMetric[];
    weaknesses: BenchmarkMetric[];
  };
  distribution?: {
    sampleSize: number;
  };
}

const { Title, Text } = Typography;
const { Search } = Input;

// Brand Selection Component
const BrandSelectionView: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { data: brands, isLoading, error } = useGetAdminBrandsForAnalyticsQuery({
    jwtToken: user?.jwtToken || '',
  });

  const handleBrandClick = (brandHash: string, brandName: string) => {
    navigate(`/admin/benchmarking/${brandHash}?brandName=${encodeURIComponent(brandName)}`);
  };

  const handleSearch = (value: string) => {
    setSearch(value.toLowerCase());
  };

  const filteredBrands = (Array.isArray(brands) ? brands : []).filter(brand =>
    search === '' ||
    brand.name.toLowerCase().includes(search) ||
    brand.industry.toLowerCase().includes(search)
  );

  const getIndustryColor = (industry: string) => {
    const colors = {
      'Technology': '#2563eb',
      'Consumer Goods': '#10b981',
      'Fashion': '#8b5cf6',
      'Healthcare': '#f59e0b',
      'Finance': '#ef4444',
      'Automotive': '#06b6d4'
    };
    return colors[industry as keyof typeof colors] || '#64748b';
  };

  if (isLoading) {
    return (
      <div className="brand-analytics-container">
        <div className="brand-analytics-loading">
          <Spin size="large" />
          <div>Loading brands for benchmarking...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="brand-analytics-container">
        <Alert
          message="Error Loading Brands"
          description="Unable to load brand list for benchmarking. Please try again later."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{
      padding: '24px',
      backgroundColor: '#f8fafc'
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <Title
              level={2}
              style={{
                margin: 0,
                marginBottom: '8px',
                fontSize: '28px',
                fontWeight: 600,
                color: '#1f2937',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              <BarChartOutlined />
              Brand Benchmarking Dashboard
            </Title>
            <Text
              type="secondary"
              style={{
                fontSize: '16px',
                color: '#6b7280'
              }}
            >
              Select a brand to view comprehensive benchmarking performance and insights
            </Text>
          </div>

          <div style={{
            textAlign: 'right',
            backgroundColor: '#f8fafc',
            padding: '12px 16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <Text
              style={{
                fontSize: '24px',
                fontWeight: 600,
                color: '#2563eb',
                display: 'block'
              }}
            >
              {filteredBrands.length}
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              brand{filteredBrands.length !== 1 ? 's' : ''} available
            </Text>
          </div>
        </div>
      </div>

      {/* Search Controls */}
      <div className="brand-analytics-controls">
        <div className="control-group">
          <label className="control-label">
            <SearchOutlined /> Search Brands
          </label>
          <Search
            placeholder="Search by brand name or industry..."
            onSearch={handleSearch}
            onChange={(e) => setSearch(e.target.value.toLowerCase())}
            className="brand-analytics-select"
            style={{ minWidth: 300 }}
          />
        </div>
      </div>

      {/* Brand Grid */}
      {filteredBrands.length === 0 ? (
        <div className="brand-analytics-empty">
          <Title level={4}>No Brands Found</Title>
          <p>No brands match your search criteria. Try adjusting your search terms.</p>
        </div>
      ) : (
        <Row gutter={[24, 24]}>
          {filteredBrands.map((brand) => (
            <Col key={brand.hash} xs={24} sm={12} md={8} lg={6}>
              <Card
                className="admin-brand-card brand-analytics-card"
                onClick={() => handleBrandClick(brand.hash, brand.name)}
                hoverable
                style={{
                  height: '420px',
                  cursor: 'pointer',
                  borderRadius: '16px',
                  boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
                  border: brand.isMock ? '2px solid #fa8c16' : '1px solid #f5f5f5',
                  backgroundColor: brand.isMock ? '#fff7e6' : '#ffffff',
                  transition: 'all 0.3s ease',
                  overflow: 'visible'
                }}
                styles={{ body: {
                    padding: '28px 24px 24px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)'
                  }
                }}
              >
                <div>
                  {/* Brand Icon */}
                  <div style={{
                    textAlign: 'center',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      width: '72px',
                      height: '72px',
                      borderRadius: '50%',
                      backgroundColor: `${getIndustryColor(brand.industry)}12`,
                      border: `2px solid ${getIndustryColor(brand.industry)}25`,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '12px',
                      boxShadow: `0 4px 12px ${getIndustryColor(brand.industry)}20`
                    }}>
                      <ShopOutlined
                        style={{
                          fontSize: '28px',
                          color: getIndustryColor(brand.industry),
                          fontWeight: 600
                        }}
                      />
                    </div>
                  </div>

                  {/* Brand Name */}
                  <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                    <Title
                      level={4}
                      style={{
                        margin: 0,
                        fontSize: '20px',
                        fontWeight: 700,
                        color: '#1a1a1a',
                        lineHeight: 1.2,
                        letterSpacing: '-0.02em'
                      }}
                    >
                      {brand.name}
                    </Title>
                    {brand.isMock && (
                      <Tag
                        color="orange"
                        style={{
                          fontSize: '10px',
                          marginTop: '4px'
                        }}
                      >
                        MOCK DATA
                      </Tag>
                    )}
                  </div>

                  {/* Industry Tag */}
                  <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    <Tag
                      style={{
                        backgroundColor: `${getIndustryColor(brand.industry)}18`,
                        color: getIndustryColor(brand.industry),
                        border: `1px solid ${getIndustryColor(brand.industry)}40`,
                        borderRadius: '20px',
                        padding: '6px 16px',
                        fontSize: '13px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {brand.industry}
                    </Tag>
                  </div>

                  {/* Organization ID */}
                  <div style={{
                    textAlign: 'center',
                    backgroundColor: '#f8f9fa',
                    padding: '6px 10px',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontWeight: 500,
                        color: '#6c757d'
                      }}
                    >
                      <ApartmentOutlined style={{ fontSize: '11px' }} />
                      ORG: {brand.organizationId}
                    </Text>
                  </div>
                </div>

                {/* Action Button */}
                <div style={{
                  borderTop: '1px solid #e9ecef',
                  paddingTop: '20px',
                  textAlign: 'center'
                }}>
                  <Button
                    type="primary"
                    icon={<BarChartOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBrandClick(brand.hash, brand.name);
                    }}
                    style={{
                      borderRadius: '10px',
                      fontWeight: 600,
                      height: '40px',
                      paddingLeft: '20px',
                      paddingRight: '20px',
                      fontSize: '14px',
                      background: getIndustryColor(brand.industry),
                      borderColor: getIndustryColor(brand.industry),
                      boxShadow: `0 4px 12px ${getIndustryColor(brand.industry)}30`
                    }}
                  >
                    View Benchmarks
                  </Button>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Benchmarking Overview Card */}
      {filteredBrands.length > 0 && (
        <Card
          title="Benchmarking Features"
          style={{ marginTop: 24 }}
          bordered={false}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card type="inner" size="small">
                <div style={{ textAlign: 'center' }}>
                  <BarChartOutlined style={{ fontSize: 24, color: 'var(--primary-color)' }} />
                  <Title level={5}>Performance Comparison</Title>
                  <Text type="secondary">
                    Compare your brand's performance against industry means and statistical distributions
                  </Text>
                </div>
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card type="inner" size="small">
                <div style={{ textAlign: 'center' }}>
                  <RiseOutlined style={{ fontSize: 24, color: 'var(--primary-color)' }} />
                  <Title level={5}>Statistical Insights</Title>
                  <Text type="secondary">
                    View detailed statistical analysis including standard deviations, percentiles, and performance categories
                  </Text>
                </div>
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card type="inner" size="small">
                <div style={{ textAlign: 'center' }}>
                  <SearchOutlined style={{ fontSize: 24, color: 'var(--primary-color)' }} />
                  <Title level={5}>Brand Equity Hierarchy</Title>
                  <Text type="secondary">
                    Benchmark across all levels from overall brand equity down to individual statement performance
                  </Text>
                </div>
              </Card>
            </Col>
          </Row>
        </Card>
      )}
    </div>
  );
};



// Main Benchmarking Detail View
const BenchmarkingDetailView: React.FC<{ brandHash: string; brandName: string }> = ({
  brandHash,
  brandName
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [timePeriod, setTimePeriod] = useState('all_time');
  const [businessTypeFilter, setBusinessTypeFilter] = useState('all');
  const [productTypeFilter, setProductTypeFilter] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState<string>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isUpdatingLatentDisplay, setIsUpdatingLatentDisplay] = useState(false);
  const [isUpdatingMeasuredDisplay, setIsUpdatingMeasuredDisplay] = useState(false);

  // Display preferences
  const { data: preferences } = useGetBenchmarkingPreferencesQuery({
    jwtToken: user?.jwtToken || '',
  });
  const [updatePreferences] = useUpdateBenchmarkingPreferencesMutation();

  const displayPreferences = preferences?.displayTypes || {
    latentElements: 'cards',
    measuredElements: 'bar_chart'
  };

  // Get brands data to find the numeric ID from the hash
  const { data: brands } = useGetAdminBrandsForAnalyticsQuery({
    jwtToken: user?.jwtToken || '',
  });

  const brand = brands?.find(b => b.hash === brandHash);
  const brandId = brand ? parseInt(brand.id) : null;

  // Fetch configurable business types (includes productTypes relation)
  const { data: businessTypesData } = useGetAdminBenchmarkBusinessTypesQuery(
    { jwtToken: user?.jwtToken || '' },
    { skip: !user?.jwtToken },
  );

  const businessTypeOptions = useMemo(() => {
    const allOption = { value: 'all', label: 'All Companies' };
    if (!businessTypesData) return [allOption];
    return [allOption, ...businessTypesData.filter((bt: any) => bt.isActive).map((bt: any) => ({ value: bt.slug, label: bt.name }))];
  }, [businessTypesData]);

  // Derive product types from the selected business type's linked product types
  const selectedBusinessTypeEntity = useMemo(() => {
    if (businessTypeFilter === 'all' || !businessTypesData) return null;
    return businessTypesData.find((bt: any) => bt.slug === businessTypeFilter) || null;
  }, [businessTypesData, businessTypeFilter]);

  const productTypeOptions = useMemo(() => {
    const allOption = { value: 'all', label: 'All Product Types' };
    if (!selectedBusinessTypeEntity?.productTypes?.length) return [allOption];
    return [allOption, ...selectedBusinessTypeEntity.productTypes.map((pt: any) => ({ value: pt.slug, label: pt.name }))];
  }, [selectedBusinessTypeEntity]);

  const isProductTypeDisabled = businessTypeFilter === 'all';

  // Reset product type when business type changes
  React.useEffect(() => {
    if (businessTypeFilter === 'all') {
      setProductTypeFilter('all');
    } else {
      const isValid = productTypeOptions.some(o => o.value === productTypeFilter);
      if (!isValid) setProductTypeFilter('all');
    }
  }, [businessTypeFilter, productTypeOptions, productTypeFilter]);

  const [calculateBenchmarks] = useCalculateBenchmarksMutation();

  const { data: benchmarkResponse, isLoading: benchmarkLoading, refetch: refetchBenchmarkData } = useGetBrandBenchmarksQuery({
    jwtToken: user?.jwtToken || '',
    brandId: brandId || 0,
    query: { timePeriod, businessType: businessTypeFilter, productType: productTypeFilter, includeHistorical: true }
  }, { skip: !brandId || !user?.jwtToken });

  const benchmarkData = useMemo(() => {
    const data = benchmarkResponse?.data;
    if (!data) return undefined;
    const mapMetrics = (metrics: BenchmarkMetric[]) =>
      metrics.map(m => ({ ...m, categoryName: getDisplayName(m.categoryName) }));
    return {
      ...data,
      brandPerformance: {
        ...data.brandPerformance,
        metrics: mapMetrics(data.brandPerformance.metrics),
        strengths: mapMetrics(data.brandPerformance.strengths),
        weaknesses: mapMetrics(data.brandPerformance.weaknesses),
      },
    };
  }, [benchmarkResponse?.data]);
  const benchmarkAvailable = benchmarkResponse?.available || false;
  const benchmarkUnavailableReason = benchmarkResponse?.reason;

  const TIME_PERIODS = [
    { value: 'all_time', label: 'All Time' },
    { value: '365_days', label: 'Last 365 Days' },
    { value: '180_days', label: 'Last 180 Days' },
    { value: '120_days', label: 'Last 120 Days' },
    { value: '90_days', label: 'Last 90 Days' },
    { value: '60_days', label: 'Last 60 Days' },
    { value: '30_days', label: 'Last 30 Days' },
  ];



  const getFilteredMetrics = (metrics: BenchmarkMetric[]): BenchmarkMetric[] => {
    if (!metrics) return [];

    // Group metrics by level to show proper hierarchy
    const level0Metrics = metrics.filter(m => m.level === 0); // Brand Equity
    const level1Metrics = metrics.filter(m => m.level === 1); // Latent elements (Brand Loyalty, etc.)
    const level2Metrics = metrics.filter(m => m.level === 2); // Measured elements (Attitudinal Loyalty, etc.)

    // Combine all levels but prioritize meaningful categories with actual data
    const allMetrics = [...level0Metrics, ...level1Metrics, ...level2Metrics];

    // Filter to remove duplicates but keep all metrics even if they have no industry mean yet
    const uniqueMetrics = new Map();
    allMetrics.forEach(metric => {
      // Include metrics even if industryMean is 0 or missing - they might not have benchmark data yet
      // Special handling for Price Deals which ranges from -100 to 0
      if (metric.value !== null && metric.value !== undefined) {
        const key = `${metric.categoryName}_${metric.level}`;
        if (!uniqueMetrics.has(key)) {
          uniqueMetrics.set(key, metric);
        }
      }
    });

    // Return all meaningful metrics (not limited to 8)
    return Array.from(uniqueMetrics.values());
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    const hideLoading = message.loading('Refreshing benchmark data...', 0);

    try {
      // First trigger benchmark calculation
      await calculateBenchmarks({
        jwtToken: user?.jwtToken || '',
        forceRecalculation: true
      }).unwrap();

      // Then refetch the benchmark data to get updated results
      await refetchBenchmarkData();

      hideLoading();
      message.success('Benchmark data refreshed successfully!');
    } catch (error) {
      console.error('Failed to refresh benchmarks:', error);
      hideLoading();
      message.error('Failed to refresh benchmark data. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLatentElementsDisplayChange = async (displayType: 'cards' | 'bar_chart' | 'polar' | 'bullet' | 'diverging' | 'slope' | 'scatter' | 'strip') => {
    setIsUpdatingLatentDisplay(true);
    try {
      const updatedPreferences = {
        ...preferences,
        displayTypes: {
          ...displayPreferences,
          latentElements: displayType,
        },
      };
      await updatePreferences({
        jwtToken: user?.jwtToken || '',
        preferences: updatedPreferences,
      }).unwrap();
    } catch (error) {
      console.error('Failed to update display preferences:', error);
      message.error('Failed to save display preference');
    } finally {
      setIsUpdatingLatentDisplay(false);
    }
  };

  const handleMeasuredElementsDisplayChange = async (displayType: 'cards' | 'bar_chart' | 'polar' | 'bullet' | 'diverging' | 'slope' | 'scatter' | 'strip') => {
    setIsUpdatingMeasuredDisplay(true);
    try {
      const updatedPreferences = {
        ...preferences,
        displayTypes: {
          ...displayPreferences,
          measuredElements: displayType,
        },
      };
      await updatePreferences({
        jwtToken: user?.jwtToken || '',
        preferences: updatedPreferences,
      }).unwrap();
    } catch (error) {
      console.error('Failed to update display preferences:', error);
      message.error('Failed to save display preference');
    } finally {
      setIsUpdatingMeasuredDisplay(false);
    }
  };

  if (benchmarkLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Check if we have meaningful benchmark data
  const hasNoMeaningfulData = !benchmarkAvailable;

  const filteredMetrics = (benchmarkData?.brandPerformance?.metrics && benchmarkAvailable)
    ? getFilteredMetrics(benchmarkData.brandPerformance.metrics)
    : [];

  if (hasNoMeaningfulData) {
    return (
      <div style={{ padding: '24px' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/benchmarking')}
          style={{ marginBottom: '24px' }}
        >
          Back to Brands
        </Button>
        <Card style={{ textAlign: 'center', padding: '48px' }}>
          <Title level={3} style={{ marginBottom: 16 }}>
            No Benchmark Data Available
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 24, fontSize: '16px' }}>
            {benchmarkUnavailableReason || "Benchmark data is not available for this brand."}
          </Text>
          <Text type="secondary" style={{ display: 'block', marginBottom: 32 }}>
            Benchmarks require data from multiple brands in the same data pool (mock or real) to provide meaningful comparisons.
          </Text>
          <div style={{ marginTop: 24 }}>
            <Button
              icon={<ReloadOutlined spin={isRefreshing} />}
              onClick={handleRefresh}
              loading={isRefreshing}
              disabled={isRefreshing}
              type="primary"
            >
              {isRefreshing ? 'Refreshing...' : 'Check for Updates'}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
      padding: '0'
    }}>
      {/* Navigation */}
      <div style={{
        padding: '20px 32px',
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/admin/benchmarking')}
              type="text"
              style={{
                padding: '8px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                background: 'white'
              }}
            />
            <div>
              <Title level={2} style={{
                margin: 0,
                fontSize: '28px',
                fontWeight: 700,
                color: '#0f172a',
                letterSpacing: '-0.025em'
              }}>
                {brandName}
              </Title>
              <Text style={{
                color: '#64748b',
                fontSize: '16px',
                fontWeight: 500
              }}>
                Brand Performance Benchmarking
              </Text>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <Select
              value={businessTypeFilter}
              onChange={setBusinessTypeFilter}
              style={{ width: 180 }}
              options={businessTypeOptions}
              size="large"
              placeholder="Business Type"
            />
            <Select
              value={productTypeFilter}
              onChange={setProductTypeFilter}
              style={{ width: 180 }}
              options={productTypeOptions}
              size="large"
              disabled={isProductTypeDisabled}
              placeholder="Product Type"
            />
            <Tooltip title="Coming soon: Filter benchmarks by country">
              <Select
                value={selectedCountry}
                onChange={setSelectedCountry}
                style={{ width: 200 }}
                size="large"
                disabled
                placeholder="Select country"
                options={[
                  { value: 'all', label: 'All Countries' }
                ]}
                suffixIcon={
                  <>
                    <GlobalOutlined style={{ marginRight: 4 }} />
                    <Badge
                      count="Soon"
                      style={{
                        backgroundColor: '#52c41a',
                        fontSize: 9,
                        height: 16,
                        lineHeight: '16px',
                        marginLeft: 4
                      }}
                    />
                  </>
                }
              />
            </Tooltip>
            <Select
              value={timePeriod}
              onChange={setTimePeriod}
              style={{ width: 160 }}
              options={TIME_PERIODS}
              size="large"
            />
            <Button
              icon={<ReloadOutlined spin={isRefreshing} />}
              onClick={handleRefresh}
              loading={isRefreshing}
              disabled={isRefreshing}
              size="large"
              type="primary"
              style={{
                background: isRefreshing
                  ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                  : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                border: 'none',
                borderRadius: '8px',
                boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.39)'
              }}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
            </Button>
          </div>
        </div>
      </div>

      {/* Hero Metrics Section */}
      <div style={{ padding: '40px 32px 56px' }}>
        {/* Main Performance Score */}
        <div style={{
          textAlign: 'center',
          marginBottom: '48px'
        }}>
          {(() => {
            const overallMetric = filteredMetrics.find(m => m.level === 0);
            // Calculate absolute difference instead of percentage difference
            const brandScore = benchmarkData?.brandPerformance?.overallScore || 0;
            const industryAvg = overallMetric?.industryMean || 0;
            const absoluteDiff = calculateAbsoluteDifference(brandScore, industryAvg);

            // Get performance level based on absolute difference
            const performanceLevel = getPerformanceLevel(absoluteDiff);

            // Map performance level to display colors with gradients
            const getBrandEquityColors = (perfLevel: typeof performanceLevel) => {
              const colorMaps = {
                'excellent': {
                  scoreColor: '#15803d',
                  badgeBg: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                  badgeBorder: '#16a34a',
                  badgeColor: '#15803d',
                  iconColor: '#15803d',
                },
                'very-good': {
                  scoreColor: '#16a34a',
                  badgeBg: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                  badgeBorder: '#22c55e',
                  badgeColor: '#16a34a',
                  iconColor: '#16a34a',
                },
                'good': {
                  scoreColor: '#22c55e',
                  badgeBg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                  badgeBorder: '#4ade80',
                  badgeColor: '#22c55e',
                  iconColor: '#22c55e',
                },
                'fair': {
                  scoreColor: '#ef4444',
                  badgeBg: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
                  badgeBorder: '#f87171',
                  badgeColor: '#ef4444',
                  iconColor: '#ef4444',
                },
                'poor': {
                  scoreColor: '#dc2626',
                  badgeBg: 'linear-gradient(135deg, #fef2f2 0%, #fca5a5 100%)',
                  badgeBorder: '#ef4444',
                  badgeColor: '#dc2626',
                  iconColor: '#dc2626',
                },
                'critical': {
                  scoreColor: '#b91c1c',
                  badgeBg: 'linear-gradient(135deg, #fee2e2 0%, #f87171 100%)',
                  badgeBorder: '#dc2626',
                  badgeColor: '#b91c1c',
                  iconColor: '#b91c1c',
                }
              };

              return {
                ...colorMaps[perfLevel.performance],
                description: perfLevel.label
              };
            };

            const colors = getBrandEquityColors(performanceLevel);
            const isPositive = absoluteDiff >= 0;

            return (
              <>
                <Text style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '12px',
                  display: 'block'
                }}>
                  Brand Equity
                </Text>
                <div style={{
                  fontSize: '96px',
                  fontWeight: 800,
                  lineHeight: 1,
                  marginBottom: '8px',
                  color: colors.scoreColor
                }}>
                  {benchmarkData?.brandPerformance?.overallScore?.toFixed(1) || '0.0'}%
                </div>
                <Text style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: colors.badgeColor,
                  marginBottom: '16px',
                  display: 'block'
                }}>
                  {colors.description}
                </Text>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '12px 24px',
                  background: colors.badgeBg,
                  borderRadius: '50px',
                  border: `2px solid ${colors.badgeBorder}`
                }}>
                  {isPositive ?
                    <RiseOutlined style={{
                      color: colors.iconColor,
                      fontSize: '20px'
                    }} /> :
                    <FallOutlined style={{
                      color: colors.iconColor,
                      fontSize: '20px'
                    }} />
                  }
                  <Text style={{
                    color: colors.badgeColor,
                    fontSize: '18px',
                    fontWeight: 700
                  }}>
                    {isPositive ? '+' : ''}{absoluteDiff.toFixed(1)}% {isPositive ? 'above' : 'below'} industry average
                  </Text>
                </div>
              </>
            );
          })()}
        </div>

        {/* Key Metrics Grid - Show all 10 Latent Elements */}
        <div style={{ padding: '0 32px', marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <Title level={4} style={{
              margin: 0,
              color: '#1e293b',
              fontSize: '22px',
              fontWeight: 700,
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
              letterSpacing: '0.5px'
            }}>
              LATENT ELEMENTS
            </Title>
            <Select
              value={displayPreferences?.latentElements || 'cards'}
              onChange={handleLatentElementsDisplayChange}
              size="small"
              style={{ width: 140 }}
              loading={isUpdatingLatentDisplay}
            >
              <Select.Option value="cards">
                <AppstoreOutlined style={{ marginRight: 6 }} />
                Cards
              </Select.Option>
              <Select.Option value="bar_chart">
                <BarChartOutlined style={{ marginRight: 6 }} />
                Chart
              </Select.Option>
              <Select.Option value="polar">
                <RadarChartOutlined style={{ marginRight: 6 }} />
                Polar
              </Select.Option>
              <Select.Option value="bullet">
                <DashboardOutlined style={{ marginRight: 6 }} />
                Bullet
              </Select.Option>
              <Select.Option value="diverging">
                <SplitCellsOutlined style={{ marginRight: 6 }} />
                Diverging
              </Select.Option>
              <Select.Option value="slope">
                <LineChartOutlined style={{ marginRight: 6 }} />
                Slope
              </Select.Option>
              <Select.Option value="scatter">
                <DotChartOutlined style={{ marginRight: 6 }} />
                Scatter
              </Select.Option>
              <Select.Option value="strip">
                <NodeIndexOutlined style={{ marginRight: 6 }} />
                Strip
              </Select.Option>
            </Select>
          </div>
          <Card style={{
            borderRadius: '16px',
            boxShadow: '0 8px 32px -8px rgba(0, 0, 0, 0.15)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '2px solid rgba(148, 163, 184, 0.2)',
            overflow: 'hidden'
          }}>
            <Spin spinning={isUpdatingLatentDisplay} tip="Updating display...">
              <div style={{
                padding: '32px',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)'
              }}>
                {(() => {
                  const latentMetrics = filteredMetrics.filter(m => m.level === 1 && m.categoryName !== 'Brand Equity');
                  const displayType = displayPreferences?.latentElements || 'cards';

                  switch (displayType) {
                    case 'cards':
                      return <MetricsCards metrics={latentMetrics} maxItems={10} />;
                    case 'bar_chart':
                      return <PerformanceChart metrics={latentMetrics} chartId="latent-elements" />;
                    case 'polar':
                      return <PolarChart metrics={latentMetrics} chartId="latent-elements-polar" />;
                    case 'bullet':
                      return <BulletChart metrics={latentMetrics} chartId="latent-elements-bullet" />;
                    case 'diverging':
                      return <DivergingBarChart metrics={latentMetrics} chartId="latent-elements-diverging" />;
                    case 'slope':
                      return <SlopeChart metrics={latentMetrics} chartId="latent-elements-slope" />;
                    case 'scatter':
                      return <ScatterPlot metrics={latentMetrics} chartId="latent-elements-scatter" />;
                    case 'strip':
                      return <StripChart metrics={latentMetrics} chartId="latent-elements-strip" />;
                    default:
                      return <MetricsCards metrics={latentMetrics} maxItems={10} />;
                  }
                })()}
              </div>
            </Spin>
          </Card>
        </div>
      </div>

      {/* Performance Comparison Chart - Full Width */}
      <div style={{ padding: '0 32px', marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <Title level={4} style={{
            margin: 0,
            color: '#1e293b',
            fontSize: '22px',
            fontWeight: 700,
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
            letterSpacing: '0.5px'
          }}>
            MEASURED ELEMENTS
          </Title>
          <Select
            value={displayPreferences?.measuredElements || 'bar_chart'}
            onChange={handleMeasuredElementsDisplayChange}
            size="small"
            style={{ width: 140 }}
            loading={isUpdatingMeasuredDisplay}
          >
            <Select.Option value="cards">
              <AppstoreOutlined style={{ marginRight: 6 }} />
              Cards
            </Select.Option>
            <Select.Option value="bar_chart">
              <BarChartOutlined style={{ marginRight: 6 }} />
              Chart
            </Select.Option>
            <Select.Option value="polar">
              <RadarChartOutlined style={{ marginRight: 6 }} />
              Polar
            </Select.Option>
            <Select.Option value="bullet">
              <DashboardOutlined style={{ marginRight: 6 }} />
              Bullet
            </Select.Option>
            <Select.Option value="diverging">
              <SplitCellsOutlined style={{ marginRight: 6 }} />
              Diverging
            </Select.Option>
            <Select.Option value="slope">
              <LineChartOutlined style={{ marginRight: 6 }} />
              Slope
            </Select.Option>
            <Select.Option value="scatter">
              <DotChartOutlined style={{ marginRight: 6 }} />
              Scatter
            </Select.Option>
            <Select.Option value="strip">
              <NodeIndexOutlined style={{ marginRight: 6 }} />
              Strip
            </Select.Option>
          </Select>
        </div>
        <Card style={{
          borderRadius: '16px',
          boxShadow: '0 8px 32px -8px rgba(0, 0, 0, 0.15)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '2px solid rgba(148, 163, 184, 0.2)',
          overflow: 'hidden'
        }}>
          <Spin spinning={isUpdatingMeasuredDisplay} tip="Updating display...">
            <div style={{
              padding: '32px',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)'
            }}>
              {(() => {
                const measuredMetrics = filteredMetrics.filter(m => m.level === 2 && m.categoryName !== 'Price Deals' && m.categoryName !== 'Non-customer Awareness');
                const displayType = displayPreferences?.measuredElements || 'bar_chart';

                switch (displayType) {
                  case 'bar_chart':
                    return <PerformanceChart metrics={measuredMetrics} chartId="measured-elements" />;
                  case 'cards':
                    return <MetricsCards metrics={measuredMetrics} />;
                  case 'polar':
                    return <PolarChart metrics={measuredMetrics} chartId="measured-elements-polar" />;
                  case 'bullet':
                    return <BulletChart metrics={measuredMetrics} chartId="measured-elements-bullet" />;
                  case 'diverging':
                    return <DivergingBarChart metrics={measuredMetrics} chartId="measured-elements-diverging" />;
                  case 'slope':
                    return <SlopeChart metrics={measuredMetrics} chartId="measured-elements-slope" />;
                  case 'scatter':
                    return <ScatterPlot metrics={measuredMetrics} chartId="measured-elements-scatter" />;
                  case 'strip':
                    return <StripChart metrics={measuredMetrics} chartId="measured-elements-strip" />;
                  default:
                    return <PerformanceChart metrics={measuredMetrics} chartId="measured-elements" />;
                }
              })()}
            </div>
          </Spin>
        </Card>
      </div>

    </div>
  );
};

// Main Component
export const AdminBenchmarkingPage: React.FC = () => {
  const { brandHash } = useParams<{ brandHash: string }>();
  const brandName = new URLSearchParams(window.location.search).get('brandName') || '';

  if (brandHash) {
    return <BenchmarkingDetailView brandHash={brandHash} brandName={brandName} />;
  }
  return <BrandSelectionView />;
};

export default AdminBenchmarkingPage;